import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Users, ChevronRight, IndianRupee, CheckCircle2, Activity, Users as FamilyIcon } from 'lucide-react';
import PatientCard from './PatientCard';
import PatientFamilyDrawer from './PatientFamilyDrawer';
import VitalsHistoryDrawer from './VitalsHistoryDrawer';
import { todayIST } from '../utils/date';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

/**
 * Compact widget shown at the top of a doctor's dashboard listing the
 * patients reception has checked in today for their session. Polls every
 * 20s so a new walk-in surfaces without a manual refresh. Clicking a
 * card opens the patient's consultation via the parent `onOpen` callback
 * (DashboardPage.openPatient).
 *
 * Each card now carries Family + Vitals drill actions powered by the
 * right-side workspace drawers.
 */
export default function DoctorQueueWidget({ user, onOpen }) {
  const today = todayIST();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [familyPatient, setFamilyPatient] = useState(null);
  const [vitalsPatient, setVitalsPatient] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data: sessions } = await ax.get(`/api/clinic-ops/sessions?date=${today}`);
      const mine = (sessions || []).filter(s => !user?.id || s.doctor_id === user.id);
      const target = mine.length ? mine : sessions;
      const all = [];
      for (const s of (target || []).slice(0, 5)) {
        try {
          const { data } = await ax.get(`/api/clinic-ops/queue?session_id=${s.id}`);
          (data || []).forEach(q => all.push({ ...q, session: s }));
        } catch { /* skip */ }
      }
      setQueue(all);
    } catch { setQueue([]); }
    setLoading(false);
  }, [today, user?.id]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 20000);
    return () => clearInterval(t);
  }, [refresh]);

  const openQueueEntry = (q) => {
    if (!onOpen) return;
    onOpen({
      patient_id: q.patient_id,
      patient_name: q.patient_name,
      age: q.patient_age || null,
      gender: q.patient_gender || '',
      appointment_id: '',
      mrn: '',
      phone: q.patient_phone || '',
      source: 'clinic_ops_queue',
      queue_entry_id: q.id,
    });
  };

  if (loading && queue.length === 0) return null;

  return (
    <div className="bg-white border rounded-xl p-4 mb-5" data-testid="doctor-queue-widget">
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-4 w-4 text-blue-700" />
        <span className="text-sm font-semibold text-zinc-900">Reception queue · today</span>
        <span className="ml-auto text-xs text-zinc-500">{queue.length} patient{queue.length === 1 ? '' : 's'}</span>
      </div>
      {queue.length === 0 ? (
        <p className="text-xs text-zinc-500 py-2">No patients in your queue yet. Reception check-ins will appear here in real time.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {queue.slice(0, 9).map(q => (
            <PatientCard
              key={q.id}
              variant="doctor"
              patient={{
                id: q.patient_id,
                name: q.patient_name || q.patient?.name || 'Patient',
                phone: q.patient_phone,
                age: q.patient_age,
                gender: q.patient_gender,
              }}
              badge={
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                    #{q.queue_no} · {q.status || 'waiting'}
                  </span>
                  {q.discount_summary && (
                    <span
                      className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${
                        q.discount_summary.waived
                          ? 'bg-amber-50 text-amber-800 border-amber-300'
                          : 'bg-indigo-50 text-indigo-700 border-indigo-200'
                      }`}
                      title={
                        `${q.discount_summary.waived ? 'Fee waived' : `${q.discount_summary.discount_percent}% off`}` +
                        (q.discount_summary.reason ? ` · ${q.discount_summary.reason}` : '') +
                        (q.discount_summary.note ? ` — ${q.discount_summary.note}` : '')
                      }
                      data-testid={`doc-queue-discount-${q.id}`}
                    >
                      {q.discount_summary.waived
                        ? '🎫 Waived'
                        : `${q.discount_summary.discount_percent}% off`}
                    </span>
                  )}
                </div>
              }
              onOpen={() => openQueueEntry(q)}
              onFamily={() => setFamilyPatient({
                id: q.patient_id, name: q.patient_name, phone: q.patient_phone,
              })}
              onVitals={() => setVitalsPatient({
                id: q.patient_id, name: q.patient_name, age: q.patient_age,
              })}
            />
          ))}
        </div>
      )}
      <PatientFamilyDrawer
        open={!!familyPatient}
        patient={familyPatient}
        onClose={() => setFamilyPatient(null)}
        onPick={(p) => openQueueEntry({
          patient_id: p.id, patient_name: p.name,
          patient_age: p.age, patient_gender: p.gender, patient_phone: p.phone,
        })}
      />
      <VitalsHistoryDrawer
        open={!!vitalsPatient}
        patient={vitalsPatient}
        onClose={() => setVitalsPatient(null)}
      />
    </div>
  );
}
